import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, Checkbox, Chip, Divider, IconButton, ListItemIcon, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Toolbar, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { useMoeda } from "../../../core/hooks/useMoeda";
import { useCadastrarEditarProdutoContext } from "../../contexts/CadastrarEditarProdutoContext";
import { IBorda } from "../../interfaces/IBorda";
import { ButtonsContainerForm, ConfiguracaoBordas, FiltroContainer, TabelaVazia } from "./styles";
import { useTamanhoTela } from '../../../core/hooks/useTamanhoTela';
import { FormularioBorda } from './containers/FormularioBorda';
import { ArrowDownward, ArrowDropDown, ArrowUpward, Kitchen, PowerSettingsNew } from '@mui/icons-material';
import { useProdutos } from '../../hooks/useProdutos';
import { ConfirmacaoAcaoModal } from '../../../core/components/ConfirmacaoAcaoModal';
import { IProduto } from '../../interfaces/IProduto';
import { MenuOrdenacao } from '../../components/MenuOrdenacao';

type Tabs = "tabela" | "formulario";

interface ConfiguracaoBordasContainerProps { }

export const ConfiguracaoBordasContainer = ({ }: ConfiguracaoBordasContainerProps) => {
    const [filtroBordas, setFiltroBordas] = useState("");
    const { bordas, adicionarBorda, excluirBorda, editarBorda, ordenarBordas, trocarPosicaoBorda, handleProximo, handleVoltar, produto, categoriaUuid } = useCadastrarEditarProdutoContext();
    const [tab, setTab] = useState<Tabs>("tabela");
    const [bordaEdicao, setBordaEdicao] = useState<IBorda | undefined>(undefined);
    const [bordasSelecionadas, setBordasSelecionadas] = useState<string[]>([]);
    const [confirmacaoExclusao, setConfirmacaoExclusao] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const { real } = useMoeda();
    const { modeloTela } = useTamanhoTela();
    const { salvar } = useProdutos();

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const limparBordasSelecionadas = () => setBordasSelecionadas([]);

    const toggleSelecionado = (uuid: string) => {
        setBordasSelecionadas(prev =>
            prev.includes(uuid) ? prev.filter(id => id !== uuid) : [...prev, uuid]
        );
    };

    const toggleSelecionarTodos = () => {
        if (bordasSelecionadas.length === bordas.length) {
            limparBordasSelecionadas();
        } else {
            setBordasSelecionadas(bordas.map(b => b.uuid));
        }
    };

    const handleTab = (tab: Tabs) => {
        setTab(tab);
        setBordaEdicao(undefined);
        limparBordasSelecionadas();
    };

    const handleAbrirEdicao = (borda: IBorda) => {
        setTab("formulario");
        setBordaEdicao(borda);
        handleClose();
    };

    const handleAbrirExclusao = () => {
        setConfirmacaoExclusao(true);
        handleClose();
    };

    const handleFecharExclusao = () => setConfirmacaoExclusao(false);

    const handleExcluirBordas = () => {
        bordasSelecionadas.forEach(uuid => {
            const borda = bordas.find(b => b.uuid === uuid);
            if (borda) excluirBorda(borda);
        });
        handleFecharExclusao();
        limparBordasSelecionadas();
    };

    const handleToggleAtivacao = async () => {
        if (!produto || !categoriaUuid || bordasSelecionadas.length !== 1) return;
        const borda = bordas.find(b => b.uuid === bordasSelecionadas[0]);
        if (!borda) return;
        const bordaAtualizada: IBorda = { ...borda, inativadoAs: borda.inativadoAs ? undefined : new Date() };
        editarBorda(bordaAtualizada);
        await salvar(categoriaUuid, {
            ...produto,
            bordas: bordas.map(b => b.uuid === borda.uuid ? bordaAtualizada : b)
        } as IProduto);
        handleClose();
    };

    const bordaSelecionada = bordasSelecionadas.length === 1 ? bordas.find(b => b.uuid === bordasSelecionadas[0]) : undefined;

    /** Posição no array que vai ser salvo — a linha pode vir da lista filtrada. */
    const indiceOrdem = (uuid: string) => bordas.findIndex(b => b.uuid === uuid);

    const renderizarLinha = (borda: IBorda) => (
        <TableRow key={borda.uuid} sx={{ opacity: borda.inativadoAs ? 0.5 : 1 }}>
            <TableCell align="center">
                <Checkbox
                    checked={bordasSelecionadas.includes(borda.uuid)}
                    onChange={() => toggleSelecionado(borda.uuid)}
                />
            </TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                <Tooltip title={filtroBordas ? "Limpe o filtro para reordenar" : "Mover para cima"}>
                    <span>
                        <IconButton
                            size="small"
                            disabled={!!filtroBordas || indiceOrdem(borda.uuid) <= 0}
                            onClick={() => trocarPosicaoBorda(borda.uuid, "cima")}
                        >
                            <ArrowUpward fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={filtroBordas ? "Limpe o filtro para reordenar" : "Mover para baixo"}>
                    <span>
                        <IconButton
                            size="small"
                            disabled={!!filtroBordas || indiceOrdem(borda.uuid) === bordas.length - 1}
                            onClick={() => trocarPosicaoBorda(borda.uuid, "baixo")}
                        >
                            <ArrowDownward fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </TableCell>
            <TableCell align="center">
                <img alt={borda.descricao} src={
                    borda?.imagens?.length
                        ? borda?.imagens[0] instanceof File ? URL.createObjectURL(borda?.imagens[0]) : borda?.imagens[0].url
                        : `${process.env.PUBLIC_URL}/images/produtos/generico.png`
                } style={{ width: "80px", height: "70px", borderRadius: "5px" }} />
            </TableCell>
            <TableCell align="center">{borda.descricao}</TableCell>
            <TableCell align="center">{real(borda.preco)}</TableCell>
            <TableCell align="center">
                <Tooltip title={borda.processosPreparoKDS?.length ? `${borda.processosPreparoKDS.length} processo(s) KDS` : 'Sem KDS configurado'}>
                    <Kitchen fontSize="small" color={borda.processosPreparoKDS?.length ? 'primary' : 'disabled'} />
                </Tooltip>
            </TableCell>
            <TableCell align="center">
                <Chip
                    label={borda.inativadoAs ? "Inativo" : "Ativo"}
                    color={borda.inativadoAs ? "default" : "success"}
                    size="small"
                />
            </TableCell>
        </TableRow>
    );

    return (
        <ConfiguracaoBordas>
            {tab == "tabela" && (
                <TableContainer variant='outlined' component={Paper} sx={{ width: "100%", maxHeight: 450, height: "100%", minHeight: 350 }}>
                    <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 }, display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="h6">Bordas</Typography>
                        <FiltroContainer>
                            {modeloTela == "desktop" && (
                                <TextField
                                    placeholder='Nome'
                                    size='small'
                                    value={filtroBordas}
                                    onChange={(e) => setFiltroBordas(e.target.value)}
                                    sx={{ width: "200px" }}
                                />
                            )}
                            <Box>
                                <Button
                                    onClick={handleClick}
                                    endIcon={<ArrowDropDown />}
                                    variant="contained"
                                    disabled={bordasSelecionadas.length <= 0}
                                >
                                    Ações
                                </Button>
                                <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                                    <MenuItem disabled>
                                        <Typography variant="inherit" noWrap>
                                            {bordasSelecionadas.length} {bordasSelecionadas.length === 1 ? "borda selecionada" : "bordas selecionadas"}
                                        </Typography>
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem disabled={bordasSelecionadas.length !== 1} onClick={() => {
                                        const borda = bordas.find(b => b.uuid === bordasSelecionadas[0]);
                                        if (borda) handleAbrirEdicao(borda);
                                    }}>
                                        <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                                        <Typography variant="inherit" noWrap>Editar</Typography>
                                    </MenuItem>
                                    <MenuItem disabled={bordasSelecionadas.length !== 1} onClick={handleToggleAtivacao}>
                                        <ListItemIcon><PowerSettingsNew fontSize="small" /></ListItemIcon>
                                        <Typography variant="inherit" noWrap>
                                            {bordaSelecionada?.inativadoAs ? "Ativar" : "Inativar"}
                                        </Typography>
                                    </MenuItem>
                                    <MenuItem onClick={handleAbrirExclusao} disabled={bordasSelecionadas.length === 0}>
                                        <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
                                        <Typography variant="inherit" noWrap>Excluir</Typography>
                                    </MenuItem>
                                </Menu>
                            </Box>
                            <MenuOrdenacao
                                titulo="Ordenar bordas"
                                desabilitado={bordas.length <= 1}
                                ordenar={(direcao) => {
                                    ordenarBordas(direcao);
                                    limparBordasSelecionadas();
                                }}
                            />
                            <Tooltip title='Crie uma nova borda'>
                                <IconButton color="primary" onClick={() => handleTab("formulario")}>
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                        </FiltroContainer>
                    </Toolbar>
                    {bordas.length ? (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center">
                                        <Checkbox
                                            checked={bordasSelecionadas.length === bordas.length && bordas.length > 0}
                                            indeterminate={bordasSelecionadas.length > 0 && bordasSelecionadas.length < bordas.length}
                                            onChange={toggleSelecionarTodos}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Define a ordem em que aparece no cardápio, no PDV e no chat. Salve o produto para gravar.">
                                            <span>Ordem</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">Imagem</TableCell>
                                    <TableCell align="center">Nome</TableCell>
                                    <TableCell align="center">Preço</TableCell>
                                    <TableCell align="center">KDS</TableCell>
                                    <TableCell align="center">Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filtroBordas
                                    ? bordas.filter(b => b.descricao.toLowerCase().includes(filtroBordas.toLowerCase())).map(renderizarLinha)
                                    : bordas.map(renderizarLinha)}
                            </TableBody>
                        </Table>
                    ) : (
                        <TabelaVazia>Nenhuma borda cadastrada</TabelaVazia>
                    )}
                </TableContainer>
            )}
            {tab == "formulario" && <FormularioBorda
                adicionarBorda={adicionarBorda}
                editarBorda={editarBorda}
                handleTab={handleTab}
                bordaEdicao={bordaEdicao}
            />}
            {tab == "tabela" && (
                <ButtonsContainerForm>
                    <Button size="medium" variant="outlined" onClick={handleVoltar}>Voltar</Button>
                    <Button size="medium" variant="contained" onClick={handleProximo}>Próximo</Button>
                </ButtonsContainerForm>
            )}
            <ConfirmacaoAcaoModal
                open={confirmacaoExclusao}
                descricao={bordasSelecionadas.length === 1 ? "Você deseja excluir a borda selecionada?" : `Você deseja excluir as ${bordasSelecionadas.length} bordas selecionadas?`}
                titulo={bordasSelecionadas.length === 1 ? "Excluir borda" : "Excluir bordas"}
                onCancelar={handleFecharExclusao}
                onConfirmar={handleExcluirBordas}
            />
        </ConfiguracaoBordas>
    );
}
